import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { WizValidators } from '@commons/validators';
import { environment } from '@environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { UpdateHasClosedModal } from '@stores/feedback/feedback.actions';
import { WINDOW } from '@wizbii/angular-utilities';
import { trackEventWithAtInternet } from '@wizbii/tracking';
import { ContactWebservice } from '@wizbii/webservices';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';

interface FeedbackData {
  isMobile: boolean;
}

@UntilDestroy()
@Component({
  templateUrl: './feedback.component.html',
  styleUrls: ['./feedback.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackComponent implements OnInit, OnDestroy {
  isMobile = true;
  private readonly categoryTrackEvent = 'Retour Experience';
  formGroup: FormGroup;

  loading$ = new BehaviorSubject<boolean>(false);
  loadingLoader$ = new BehaviorSubject<boolean>(false);

  trackEvent = trackEventWithAtInternet;

  get experience(): FormControl {
    return this.formGroup.get('experience') as FormControl;
  }

  get email(): FormControl {
    return this.formGroup.get('email') as FormControl;
  }

  constructor(
    private readonly store: Store,
    private readonly contactWebService: ContactWebservice,
    private readonly fb: FormBuilder,
    readonly dialogRef: MatDialogRef<FeedbackComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FeedbackData,
    @Inject(WINDOW) private readonly window: any
  ) {
    this.isMobile = data.isMobile;

    this.formGroup = this.fb.group({
      experience: [null, [Validators.required]],
      email: [null, [Validators.required, WizValidators.email]],
    });
  }

  // tslint:disable: no-duplicate-string
  ngOnInit(): void {
    this.trackEvent({
      category: this.categoryTrackEvent,
      action: 'Display Modal',
      type: 'action',
    });

    if (this.isMobile) {
      this.dialogRef.addPanelClass('wizbii-mobile-panel-open');

      this.dialogRef
        .backdropClick()
        .pipe(untilDestroyed(this))
        .subscribe({ next: () => this.closeDialog('Backdrop') });
    }
  }

  clickCross(): void {
    this.loading$.pipe(take(1)).subscribe({
      next: (loading) => {
        if (loading) {
          this.closeDialog('Cross End');
        } else {
          this.closeDialog('Cross Start');
        }
      },
    });
  }

  closeDialog(label: string): void {
    this.store.dispatch(new UpdateHasClosedModal(true));

    this.trackEvent({
      category: this.categoryTrackEvent,
      action: 'Close Modal',
      type: 'action',
      label,
    });

    if (this.isMobile) {
      this.dialogRef.addPanelClass('wizbii-mobile-panel-close');
      this.window.setTimeout(() => {
        this.dialogRef.close();
      }, 100);
    } else {
      this.dialogRef.close();
    }
  }

  onSubmit(): void {
    if (this.formGroup.invalid) {
      return;
    }

    this.trackEvent({
      category: this.categoryTrackEvent,
      action: 'Send Experience',
      type: 'action',
    });

    this.loadingLoader$.next(true);
    this.loading$.next(true);

    this.contactWebService
      .send({
        appId: environment.applicationId,
        firstName: '',
        lastName: '',
        phone: '',
        subject: "Nouveau retour d'expérience",
        mustSendCopy: false,
        email: this.email.value,
        content: this.experience.value,
        ...{
          recipientEmail: environment.platform === 'prod' ? 'jeunes@1jeune1solution.fr' : 'moulteampass@wizbii.com',
        },
        attributes: {
          templateKey: 'retour_xp_avril',
        },
      })
      .subscribe({
        next: () => {
          this.displayConfirmation();
        },
      });
  }

  private displayConfirmation(): void {
    this.window.setTimeout(() => {
      this.loadingLoader$.next(false);
    }, 1000);
  }

  ngOnDestroy(): void {
    this.loading$.complete();
    this.loadingLoader$.complete();
  }
}
